import { useState } from "react";
import { Link } from "react-router-dom";
import NetworkGraph from "../../components/NetworkGraph";
import Reveal from "../../components/Reveal";
import { IconDatabase } from "../../components/Icons";

const ACCENT = "#3ABEFF";

const FAQS = [
  {
    question: "O que acontece se o backup falhar?",
    answer:
      "Nossa equipe recebe alertas automáticos e age imediatamente para corrigir qualquer falha. Você é informado e o problema é resolvido sem que você precise fazer nada.",
  },
  {
    question: "Posso restaurar só um arquivo específico?",
    answer:
      "Sim. A restauração pode ser feita por arquivo, pasta, sistema inteiro ou ponto de recuperação específico — dependendo do plano contratado.",
  },
  {
    question: "O backup cobre servidores e computadores?",
    answer:
      "Sim. O sepolBACKUP cobre estações de trabalho, servidores, bancos de dados e outras fontes de dados. O escopo é definido na proposta.",
  },
];

const FEATURES = [
  {
    title: "100% Automático",
    desc: "Backups agendados sem nenhuma intervenção manual necessária",
  },
  {
    title: "Monitoramento Contínuo",
    desc: "A sepolTECH monitora e valida cada backup realizado",
  },
  {
    title: "Restauração Rápida",
    desc: "Recupere arquivos, pastas ou sistemas completos quando precisar",
  },
  {
    title: "Armazenamento em Nuvem",
    desc: "Dados replicados em nuvem com redundância e segurança",
  },
  {
    title: "Relatórios Periódicos",
    desc: "Você recebe relatórios confirmando que seus backups estão funcionando",
  },
  {
    title: "Suporte Incluído",
    desc: "Qualquer problema com backup, nossa equipe age imediatamente",
  },
];

export default function SepolBackup() {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq((prev) => (prev === index ? null : index));
  };

  return (
    <>
      {/* ═══════════════ ABERTURA ═══════════════ */}
      <section className="page-hero">
        <div className="page-hero__bg"></div>
        <div className="page-hero__grid"></div>
        <NetworkGraph variant="hero" className="page-hero__network" />
        <div className="container">
          <div className="breadcrumb animate-up">
            <Link to="/">Início</Link>
            <span>/</span>
            <Link to="/produtos/produtos">Produtos</Link>
            <span>/</span>
            <span>sepolBACKUP</span>
          </div>
          <div className="tag animate-up animate-up--d1" style={{ color: ACCENT, background: "rgba(58,190,255,.12)", borderColor: `${ACCENT}40` }}>
            <IconDatabase style={{ width: "13px", height: "13px" }} />
            BaaS · Backup-as-a-Service
          </div>
          <h1
            className="animate-up animate-up--d2"
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(40px,6vw,72px)",
              fontWeight: 700,
              letterSpacing: "-.03em",
              lineHeight: "1.05",
              marginBottom: "24px",
              color: "var(--text-primary)",
            }}
          >
            sepol<span style={{ color: ACCENT }}>BACKUP</span>
          </h1>
          <p
            className="animate-up animate-up--d3"
            style={{
              fontSize: "18px",
              color: "var(--text-secondary)",
              maxWidth: "540px",
              lineHeight: "1.7",
              marginBottom: "40px",
            }}
          >
            Backup gerenciado em nuvem. Acontece automaticamente — você não
            precisa lembrar de nada. A sepolTECH gerencia, monitora e valida
            seus backups.
          </p>
          <div className="hero-actions-col animate-up animate-up--d4">
            <Link to="/contato" className="btn btn-primary">
              Contratar sepolBACKUP →
            </Link>
            <Link to="/contato" className="btn btn-outline">
              Falar com especialista
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════ RECURSOS INCLUSOS ═══════════════ */}
      <section className="section">
        <div className="container">
          <Reveal>
            <div className="tag" style={{ color: ACCENT, background: "rgba(58,190,255,.12)", borderColor: `${ACCENT}40` }}>
              Recursos inclusos
            </div>
            <h2 className="section-title" style={{ marginBottom: "36px" }}>
              O que você recebe com <span style={{ color: ACCENT }}>sepolBACKUP</span>
            </h2>
            <div className="feature-list">
              {FEATURES.map((feature) => (
                <div className="feature-item" key={feature.title}>
                  <div
                    className="feature-item__icon"
                    style={{ background: "rgba(58,190,255,.12)", color: ACCENT }}
                  >
                    ✓
                  </div>
                  <div>
                    <strong style={{ color: "var(--text-primary)" }}>{feature.title}</strong> —{" "}
                    {feature.desc}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════ FAQ ═══════════════ */}
      <section className="section section--alt">
        <div className="container" style={{ maxWidth: "760px" }}>
          <Reveal className="text-center" style={{ marginBottom: "40px" }}>
            <div className="tag" style={{ color: ACCENT, background: "rgba(58,190,255,.12)", borderColor: `${ACCENT}40` }}>
              FAQ
            </div>
            <h2 className="section-title">
              Dúvidas sobre <span style={{ color: ACCENT }}>sepolBACKUP</span>
            </h2>
          </Reveal>

          {FAQS.map((faq, index) => (
            <Reveal
              className={`faq-item${openFaq === index ? " open" : ""}`}
              delay={index * 0.05}
              key={faq.question}
            >
              <button
                className="faq-btn"
                onClick={() => toggleFaq(index)}
                aria-expanded={openFaq === index}
              >
                {faq.question}
                <span className="faq-btn__icon">+</span>
              </button>
              <div className="faq-answer">
                <p>{faq.answer}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ═══════════════ CTA ═══════════════ */}
      <section className="section">
        <div className="container">
          <Reveal className="cta-banner">
            <h2 className="cta-banner__title">
              Seus dados protegidos, sem você lembrar de nada
            </h2>
            <p className="cta-banner__sub">
              Backup que funciona de verdade, gerenciado pela sepolTECH.
            </p>
            <div className="cta-banner__actions">
              <Link to="/contato" className="btn btn-primary">
                Contratar sepolBACKUP →
              </Link>
              <Link to="/produtos/produtos" className="btn btn-outline">
                Ver outros produtos
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
